import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl, useParams } from 'umi';
import { Segment } from 'tea-component';
import { LANGUAGE_EN } from '@home/_config/config';
import { selectValByCondition } from '@home/utils/common';
import { PR_HUB_TAB_ID_SET, PR_WORKSPACE_TABS_TOOLBAR_HOST_ID, type TPrHubTabId } from '@home/pages/studio/pr/prHubSubTabs';
import './index.scss';

export interface IPrWorkspaceTabMeta {
  id: TPrHubTabId;
  tabName: string;
  tabNameCN: string;
}

export interface IPrWorkspaceTabsProps {
  data?: Record<string, unknown>;
  /** 由 PrStudioMonthFilter 注入，需继续传给 Tab 内 Service，否则 overview/trend 等请求缺少时间参数 */
  filterVal?: unknown;
  tabList?: IPrWorkspaceTabMeta[];
  children?: React.ReactNode;
}

type PrWorkspaceTabContextValue = {
  activeTabId: TPrHubTabId;
};

const PrWorkspaceTabContext = createContext<PrWorkspaceTabContextValue>({
  activeTabId: 'overview',
});

export function usePrWorkspaceActiveTab(): TPrHubTabId {
  return useContext(PrWorkspaceTabContext).activeTabId;
}

export interface IPrTabPanelProps {
  tabId: TPrHubTabId;
  filterVal?: unknown;
  children?: React.ReactNode;
}

/** 与 PrWorkspaceTabs 配合：仅渲染当前激活 Tab 下的内容，避免非激活 Tab 内 Service 重复请求 */
export const PrTabPanel: React.FC<IPrTabPanelProps> = (props) => {
  const { tabId, children, filterVal } = props;
  const active = usePrWorkspaceActiveTab();
  if (active !== tabId) {
    return null;
  }
  return (
    <div className="dbh-pr-workspace-tabs__tab-panel">
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(child, { filterVal } as Partial<Record<string, unknown>>)
          : child,
      )}
    </div>
  );
};

function normalizePrTabParam(raw?: string): TPrHubTabId {
  const v = (raw || 'overview').toLowerCase();
  return (PR_HUB_TAB_ID_SET.has(v) ? v : 'overview') as TPrHubTabId;
}

const PrWorkspaceTabs: React.FC<IPrWorkspaceTabsProps> = (props) => {
  const { tabList = [], data, children, filterVal } = props;
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const { gameCode } = useParams<{
    gameCode?: string;
  }>();

  const routeGame = (gameCode || '').trim();
  const fallbackGame = String(data?.prHubGameCode ?? '').trim();
  const effectiveGame = routeGame || fallbackGame;

  const [activeTabId, setActiveTabId] = useState<TPrHubTabId>('overview');

  /** 切换左侧游戏时回到概览，避免与新区块内容错位 */
  useEffect(() => {
    setActiveTabId('overview');
  }, [effectiveGame]);

  const segmentOptions = useMemo(
    () =>
      tabList.map((t) => ({
        value: t.id,
        text: selectValByCondition(isEn, t.tabName, t.tabNameCN),
      })),
    [tabList, isEn],
  );

  const onSegmentChange = (val: string) => {
    setActiveTabId(normalizePrTabParam(val));
  };

  const ctx = useMemo(() => ({ activeTabId }), [activeTabId]);

  return (
    <PrWorkspaceTabContext.Provider value={ctx}>
      <div className="dbh-pr-workspace-tabs">
        <div className="dbh-pr-workspace-tabs__header">
          <div className="dbh-pr-workspace-tabs__header-row">
            <div className="dbh-pr-workspace-tabs__segment">
              <Segment
                key={`pr-workspace-segment-${effectiveGame}`}
                className={isEn ? 'segment-light' : 'segment-light segment-light-china'}
                value={activeTabId}
                onChange={onSegmentChange}
                options={segmentOptions}
              />
            </div>
            <div
              id={PR_WORKSPACE_TABS_TOOLBAR_HOST_ID}
              className="dbh-pr-workspace-tabs__toolbar-host"
            />
          </div>
        </div>
        <div className="dbh-pr-workspace-tabs__body">
          {React.Children.map(children, (child) =>
            React.isValidElement(child)
              ? React.cloneElement(child, { filterVal } as Partial<Record<string, unknown>>)
              : child,
          )}
        </div>
      </div>
    </PrWorkspaceTabContext.Provider>
  );
};

export default PrWorkspaceTabs;
